import { useState, useRef, useCallback, useEffect } from 'react';
import { cleanSpeechTranscript } from '../utils/speechCleaner';
import { useVoiceEngine, IS_MOBILE } from '../hooks/useVoiceEngine';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { SignOut, PaperclipHorizontal, Plus, ArrowsIn, ChatTeardropDots, Microphone, CaretDown } from '@phosphor-icons/react';
import { signalAthenaAuthExpired, useAuth } from '../context/AuthContext';
import { NeuralOrb } from '../components/NeuralOrb';
import { CoreRadialMenu } from '../components/CoreRadialMenu';
import { UploadWindow } from '../components/UploadWindow';
import { ValleyOfMemories } from '../components/ValleyOfMemories';
import { HolographicLocation } from '../components/HolographicLocation';
import { useOverlayPersistence } from '../hooks/useOverlayPersistence';
import { ChatWindow } from '../components/ChatWindow';
import { AthenaLogo } from '../components/AthenaLogo';
import { AthenaWorkspaceBg } from '../components/AthenaWorkspaceBg';
import { AthenaCognitiveSpace } from '../components/AthenaCognitiveSpace';
import { AthenaThinkingVideoBackground } from '../components/AthenaThinkingVideoBackground';
import { HomeVoiceComposer } from '../components/HomeVoiceComposer';
import { ImageGenerationCard } from '../components/ImageGenerationCard';
import { usePendingDeviceContext } from '../capabilities/PendingDeviceContext';
import { useAthenaDeviceContext } from '../context/AthenaDeviceContextProvider';
import axios from 'axios';
import { loadSocialPrefs } from '../utils/socialBehaviorManager';
import { parseNotifFromResponse, startContextualScheduler } from '../utils/athenaNotifications';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { API_BASE, IS_NATIVE_APP } from '../config/api';
import {
  getAthenaNotificationStatus,
  registerAthenaNotifications,
  requestAthenaNotificationsPermission,
  testAthenaNotifications,
} from '../notifications/AthenaNotifications';
import { playAthenaAudio, requestAthenaTts } from '../voice/athenaTts';
import { ATHENA_INTENT_TYPES, useAthenaIntent } from '../platform/AthenaIntentRouter';
import { QuickAthenaPanel, isQuickAthenaIntent } from '../components/QuickAthenaPanel';
import { detectAthenaOperationalIntent, executeAthenaOperationalAction } from '../capabilities/AthenaActionOrchestrator';
import { isAthenaSessionFailure, normalizeAthenaApiFailure } from '../utils/athenaApiFailure';
import { setAthenaFloatingOverlayVisualState } from '../platform/AthenaFloatingOverlay';
import { registerAthenaQuickOverlayMessageHandler } from '../platform/AthenaQuickOverlayRuntime';
import { LifeOSProvider } from '../life-os';
import { HomeContextCards } from '../life-os/HomeContextCards';

const API = API_BASE;
const ACTIVE_SESSION_STORAGE_KEY = 'athena_active_session_id';

// Home action bar: recursos temporariamente ocultos da UI da Home (implementação preservada,
// handlers e state intactos — reative trocando para `true` quando o recurso voltar).
const HOME_ACTION_BAR_FEATURES = {
  attachments: false, // clipe/anexo (setShowUpload)
  visualMode: false,  // olho/visão de tela (toggleScreen)
  fullscreen: false,  // expandir/overlay ambiente (setIsOverlay)
};

function getTimeGreeting() {
  const h = new Date().getHours();
  if (h >= 6 && h < 12) return 'Bom dia';
  if (h >= 12 && h < 18) return 'Boa tarde';
  if (h >= 18 && h < 23) return 'Boa noite';
  return 'Boa madrugada';
}

function stripEmojis(t) {
  return t
    .replace(/[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F1E0}-\u{1F1FF}\u{2702}-\u{27B0}\u{24C2}-\u{1F251}\u{1f926}-\u{1f937}\u{10000}-\u{10ffff}\u{2600}-\u{2B55}\u{200d}\u{23cf}\u{23e9}\u{231a}\u{fe0f}\u{3030}]+/gu, '')
    // Fallback local: nunca entregar Markdown ou asteriscos ao TTS.
    .replace(/[*_`#>|]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

// Normalize: lowercase + remove diacritics
function norm(s) {


  return (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

// Voice states
const VOICE_PASSIVE = 'passive';
const VOICE_ACTIVE = 'active';
const VOICE_PROCESSING = 'processing';
const VOICE_SPEAKING = 'speaking';
const VOICE_SILENT = 'silent';
const VOICE_FOCUS = 'focus';

function playManualVoiceCue(kind) {
  // No APK, os tons confirmados são produzidos pelo plugin nativo. Mantemos
  // esta versão leve somente como fallback para navegador/PWA, sem duplicar o
  // som a cada toque no Android.
  if (IS_NATIVE_APP || typeof window === 'undefined') return;

  try {
    const AudioContextConstructor = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextConstructor) return;

    const context = new AudioContextConstructor();
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    const now = context.currentTime;
    const isStart = kind === 'start';
    const duration = isStart ? 0.11 : 0.14;

    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(isStart ? 620 : 780, now);
    oscillator.frequency.exponentialRampToValueAtTime(isStart ? 840 : 520, now + duration);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(isStart ? 0.045 : 0.055, now + 0.016);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
    oscillator.connect(gain);
    gain.connect(context.destination);
    oscillator.start(now);
    oscillator.stop(now + duration + 0.015);
    oscillator.addEventListener('ended', () => {
      const closing = context.close?.();
      if (closing?.catch) void closing.catch(() => {});
    }, { once: true });
    if (context.state === 'suspended') void context.resume().catch(() => {});
  } catch {
    // Feedback de áudio é complementar: uma política do aparelho nunca deve
    // impedir a captura de voz.
  }
}

function isEditableElement(element) {
  return element instanceof HTMLElement
    && (element.matches('input, textarea, [contenteditable="true"]'));
}

function quickAthenaMessage(intent, text) {
  const content = String(text || '').trim();
  if (!content) return '';
  switch (intent?.type) {
    case ATHENA_INTENT_TYPES.NEW_TASK:
      return /\b(?:crie|criar|adicione|adicionar|registre|registrar|anote|anotar|coloque|colocar)\s+(?:uma\s+)?tarefa\b/i.test(content)
        ? content
        : `Crie uma tarefa: ${content}`;
    case ATHENA_INTENT_TYPES.NEW_REMINDER:
      return /\b(?:lembre|lembrar|lembrete|alarme|desperte|despertador)\b/i.test(content)
        ? content
        : `Crie um lembrete: ${content}`;
    case ATHENA_INTENT_TYPES.CAPTURE_IDEA:
    case ATHENA_INTENT_TYPES.IMPORT_CONTENT:
      return /\b(?:salve|guarde)\b/i.test(content)
        ? content
        : `Salve isto: ${content}`;
    default:
      return content;
  }
}

/**
 * Mantém o compositor no topo do teclado nos dois comportamentos existentes
 * de WebView Android: teclado que sobrepõe a janela e teclado que a reduz.
 */
function useHomeKeyboardPosition() {
  const [keyboard, setKeyboard] = useState({ open: false, offset: 0 });
  const baselineHeightRef = useRef(0);
  const nativeHeightRef = useRef(0);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return undefined;

    const viewport = window.visualViewport;
    baselineHeightRef.current = Math.max(window.innerHeight || 0, viewport?.height || 0);
    let frame = 0;

    const apply = () => {
      if (frame) window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        const layoutHeight = window.innerHeight || 0;
        const visual = window.visualViewport;
        const visualGap = visual
          ? Math.max(0, layoutHeight - visual.height - visual.offsetTop)
          : 0;
        const focusedEditor = isEditableElement(document.activeElement);
        const baseline = Math.max(baselineHeightRef.current, layoutHeight);
        baselineHeightRef.current = baseline;
        const layoutWasResized = focusedEditor && baseline - layoutHeight > 80;
        const reportedHeight = Math.max(nativeHeightRef.current, visualGap);
        const open = reportedHeight > 80 || layoutWasResized;
        const offset = open && !layoutWasResized ? Math.round(reportedHeight) : 0;

        document.documentElement.style.setProperty('--athena-home-keyboard-offset', `${offset}px`);
        document.documentElement.dataset.athenaHomeKeyboard = open ? 'open' : 'closed';
        setKeyboard((previous) => (
          previous.open === open && previous.offset === offset ? previous : { open, offset }
        ));
      });
    };

    const onNativeKeyboard = (event) => {
      nativeHeightRef.current = Math.max(0, Number(event.detail?.height) || 0);
      apply();
    };
    const onViewportChange = () => apply();
    const onOrientationChange = () => {
      nativeHeightRef.current = 0;
      baselineHeightRef.current = window.innerHeight || 0;
      apply();
    };

    window.addEventListener('athena-native-keyboard', onNativeKeyboard);
    window.addEventListener('resize', onViewportChange);
    window.addEventListener('orientationchange', onOrientationChange);
    viewport?.addEventListener('resize', onViewportChange);
    viewport?.addEventListener('scroll', onViewportChange);

    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener('athena-native-keyboard', onNativeKeyboard);
      window.removeEventListener('resize', onViewportChange);
      window.removeEventListener('orientationchange', onOrientationChange);
      viewport?.removeEventListener('resize', onViewportChange);
      viewport?.removeEventListener('scroll', onViewportChange);
      document.documentElement.style.setProperty('--athena-home-keyboard-offset', '0px');
      delete document.documentElement.dataset.athenaHomeKeyboard;
    };
  }, []);

  return keyboard;
}

// ── Correção 04: Thinking State — pools de pensamentos internos ───────────────
const _TP_GENERIC = [
  'organizando as ideias...', 'lembrando da conversa...', 'procurando a melhor resposta...',
  'isso é interessante...', 'comparando possibilidades...', 'fazendo uma última revisão...',
  'já entendi a direção...', 'quase pronto...', 'isso pode ajudar...', 'acho que encontrei...',
  'conectando os pontos...', 'pensando na melhor forma...',
];
const _TP_RECIPE = [
  'pensando em algo fácil...', 'procurando ingredientes simples...', 'montando a receita...', 'vendo o que combina...',
];
const _TP_CODE = [
  'revisando o código...', 'procurando o erro...', 'comparando abordagens...', 'pensando na melhor solução...', 'analisando a lógica...',
];
const _TP_PLAN = [
  'organizando prioridades...', 'montando uma sequência...', 'vendo as dependências...', 'pensando nos próximos passos...', 'estruturando o plano...',
];
const _THOUGHT_POOLS = { recipe: _TP_RECIPE, code: _TP_CODE, plan: _TP_PLAN, generic: _TP_GENERIC };
function _detectThoughtCtx(text) {
  const t = (text || '').toLowerCase();
  if (/receita|ingrediente|cozinha|aliment|comida|refei/.test(t)) return 'recipe';
  if (/código|code|erro|debug|fun[çc][aã]|variável|programa|script|deploy/.test(t)) return 'code';
  if (/plano|planejamento|organiz|cronograma|sequência|prioridade|agenda|etapa/.test(t)) return 'plan';
  return 'generic';
}

export default function AthenaPage() {
  const { user, logout } = useAuth();
  const { items: pendingDeviceItems, consume: consumePendingDeviceContext, remove: removePendingDeviceContext } = usePendingDeviceContext();
  const { prepareChatContext } = useAthenaDeviceContext();
  const { pendingIntent, consumeIntent, dismissIntent } = useAthenaIntent();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const sendMessageRef = useRef(null);
  const {
    voiceState,
    continuousMode,
    voiceLevel = 0,
    startPassive, startActive, startManual, toggleManual, finishActive, startSpeaking, doneSpeaking, finishResponseWithoutSpeech,
    cancelSpeaking, stop, mute, setProcessing, setIdle, setContinuousMode, setFocusMode, beginBargeIn,
  } = useVoiceEngine({
    context: 'home',
    initialContinuousMode: false,
    onDispatch: async (rawText) => {
      const { cleanTranscript, isCorrupted, confidence } = cleanSpeechTranscript(rawText);
      const unusableTranscript = !cleanTranscript
        || (isCorrupted && confidence < 0.35);
      if (unusableTranscript) {
        // No Manual, o segundo toque já finalizou a captura. Sem texto válido,
        // não há mensagem para enviar nem resposta para aguardar: volta pronto
        // para uma nova gravação, sem deixar a interface em "Processando".
        finishResponseWithoutSpeech();
        setHomeToast('Não consegui entender essa fala com segurança. Tente novamente em um ambiente mais silencioso ou envie por texto.');
        return;
      }
      const send = sendMessageRef.current;
      if (!send) {
        finishResponseWithoutSpeech();
        setHomeToast('A conversa ainda está preparando. Tente falar novamente em instantes.');
        return;
      }
      try {
        const outcome = await send(cleanTranscript, { fromVoice: true });
        // The engine enters PROCESSING before dispatching a transcript. A
        // refusal before a chat request starts (lock, de-dupe or another
        // queued message) must explicitly close that voice turn.
        if (outcome?.accepted === false) {
          finishResponseWithoutSpeech();
        }
      } catch {
        finishResponseWithoutSpeech();
        setHomeToast('Não consegui iniciar essa mensagem de voz. Tente novamente.');
      }
    },
    onWakeWord: (command) => {
      if (command.length > 3) sendMessageRef.current?.(command);
      else startActive();
    },
    onCaptureFailure: (failure) => {
      const denied = ['not-allowed', 'service-not-allowed'].includes(failure?.reason);
      const recognizerPermissionError = failure?.reason === 'recognizer_permission_error';
      const unavailable = failure?.reason === 'recognition_unavailable'
        || !failure?.speechRecognitionSupported;
      setHomeToast(
        denied
          ? 'A Athena não recebeu acesso ao microfone. Libere a permissão do microfone e tente novamente.'
          : recognizerPermissionError
            ? 'A permissão está ativa, mas o serviço de voz do Android não conseguiu acessar o áudio. Verifique o interruptor global de microfone e o reconhecimento de voz do aparelho.'
          : unavailable
            ? 'O reconhecimento de voz não está disponível neste navegador. Abra a Athena no Chrome atualizado.'
            : 'Não consegui transcrever sua fala. Verifique a conexão e tente falar novamente.',
      );
    },
  });
  const continuousModeRef = useRef(false);
  const [sessionId, setSessionId] = useState(null);
  const [textInput, setTextInput] = useState('');
  const [showUpload, setShowUpload] = useState(false);
  const [showValley, setShowValley] = useState(false);
  const [showLocation, setShowLocation] = useState(false);
  const [locationData, setLocationData] = useState(null);
  const [isOverlay, setIsOverlay] = useState(false);
  const [lastResponse, setLastResponse] = useState('');
  const [lastResponseIsError, setLastResponseIsError] = useState(false);
  const [lastFailedMessage, setLastFailedMessage] = useState('');
  const [lastGeneratedImage, setLastGeneratedImage] = useState(null);
  const [showChat, setShowChat] = useState(false);
  const [coreMenuOpen, setCoreMenuOpen] = useState(false);
  const [wakeProgress, setWakeProgress] = useState(0);   // 0→1 during orb wake hold
  const [wakeActive,   setWakeActive]   = useState(false);
  const [wakeMode,     setWakeMode]     = useState('voice'); // 'voice' | 'nav'
  const [thinkingThought,  setThinkingThought]  = useState('');
  const [thinkingVisible,  setThinkingVisible]  = useState(false);
  const [dragReady,    setDragReady]    = useState(false); // Phase 2: blur + arrows visible
  const [dragTarget,   setDragTarget]   = useState(null); // Phase 2: 'workspace'|'visual-call'|null

  const currentAudioRef = useRef(null);
  const ttsAbortRef = useRef(null);
  const voiceStateRef = useRef(VOICE_PASSIVE);  // sync mirror para setInterval callbacks
  const messagesEndRef = useRef(null);
  const speakingLockRef = useRef(false);
  const speechQueueRef = useRef([]);
  // Invalida TTS pendente quando a pessoa interrompe a fala ou sai da Home.
  // Isso impede que um áudio iniciado aqui atravesse para a Workspace.
  const speechGenerationRef = useRef(0);
  const pageActiveRef = useRef(true);
  const processingLockRef = useRef(false);  // prevents duplicate sendMessage
  const typeTimerRef      = useRef(null);   // Correção 05: typewriter animation timer
  const lastSentRef  = useRef({ text: '', ts: 0 });  // de-dupe identical messages
  const cogTypeRef   = useRef('generic');             // thinkingType for CognitiveSpace
  const nextThinkingVideoRef = useRef(0);
  const reentryDoneRef = useRef(false);
  // Presence Core: impede chamadas simultâneas no mesmo ciclo React/StrictMode.
  // O Worker também mantém a deduplicação persistente entre recargas e aparelhos.
  const ambientInFlightRef = useRef(false);
  const ambientBootDoneRef = useRef(false);
  // [SessionLifecycle] Session race condition fix
  const sessionIdRef = useRef(null);
  const pendingCalendarRequestRef = useRef('');
  const pendingTaskRequestRef = useRef('');
  const sessionRestoringRef = useRef(true); // true until restoration completes
  const [sessionRestoring, setSessionRestoring] = useState(true);
  const [sessionReady, setSessionReady] = useState(false);
  const restoreGenerationRef = useRef(0);
  const pendingMessageRef = useRef(null);
  // Orb double-tap + hold gesture state
  const lastTapAtRef   = useRef(0);    // timestamp of previous tap
  const holdingRef     = useRef(false);
  const wakeRafRef     = useRef(null);
  const wakeStartRef   = useRef(0);
  const wakeActivateRef = useRef(null); // always-fresh callback, avoids stale closures in RAF
  // Phase 2: drag navigation refs
  const dragHoldTimerRef   = useRef(null);
  const dragStartXRef      = useRef(0);
  const dragStartYRef      = useRef(0);
  const isDraggingRef      = useRef(false);
  const dragTargetRef      = useRef(null);
  const lastBgTapRef        = useRef(0); // for double-tap-to-cancel nav mode
  // Performance refs — position/scale applied directly to DOM, no setState per frame
  const orbContainerRef    = useRef(null);
  const dragPosRef         = useRef({ x: 0, y: 0 });
  const dragScaleRef       = useRef(1);
  const primaryPointerRef  = useRef(null);
  const pinchStartDistRef  = useRef(0);
  const pinchStartScaleRef = useRef(1);
  // Pointer Events e Touch Events não têm o mesmo comportamento em todos os
  // navegadores/PWAs móveis. Este registro impede que os dois eventos do mesmo
  // toque alternem o microfone duas vezes.
  const lastOrbActivationRef = useRef(0);
  // A long press opens the radial menu; the ref prevents its trailing click
  // from also toggling microphone capture.
  const coreMenuHoldRef = useRef(null);
  const coreMenuTriggeredRef = useRef(false);

  const screenStreamRef = useRef(null);
  const screenVideoRef  = useRef(document.createElement('video'));
  const screenCanvasRef = useRef(document.createElement('canvas'));
  const [screenActive,    setScreenActive]    = useState(false);
  const [showMobileMenu,  setShowMobileMenu]  = useState(false);
  const [homeToast,       setHomeToast]       = useState('');
  const controlledWinRef = useRef(null);
  const [chatBgState,    setChatBgState]    = useState('idle');
  const [showCognitive, setShowCognitive] = useState(false);
  const [thinkingVideoIndex, setThinkingVideoIndex] = useState(0);
  const [notificationPermission, setNotificationPermission] = useState('checking');
  const [nativePushEnabled, setNativePushEnabled] = useState(false);
  const [voiceMode, setVoiceMode] = useState('manual');
  const [showVoiceModeMenu, setShowVoiceModeMenu] = useState(false);
  const homeTextInputRef = useRef(null);
  const { open: homeKeyboardOpen } = useHomeKeyboardPosition();
  const manualCaptureActive = voiceMode === 'manual' && voiceState === VOICE_ACTIVE;
  const quickAthenaActive = isQuickAthenaIntent(pendingIntent);

  const greeting = getTimeGreeting();
  const token = localStorage.getItem('athena_token');

  useEffect(() => { voiceStateRef.current   = voiceState;     }, [voiceState]);
  useEffect(() => { continuousModeRef.current = continuousMode; }, [continuousMode]);

  // The system overlay receives only this bounded visual hint. It remains a
  // launcher: no prompt, transcript, response, token or microphone stream is
  // ever sent across the WebView/native boundary.
  useEffect(() => {
    if (!IS_NATIVE_APP) return;
    const visualState = voiceState === VOICE_ACTIVE
      ? 'listening'
      : voiceState === VOICE_PROCESSING
        ? 'thinking'
        : voiceState === VOICE_SPEAKING
          ? 'speaking'
          : voiceState === VOICE_FOCUS
            ? 'focus'
            : 'idle';
    void setAthenaFloatingOverlayVisualState(visualState);
  }, [voiceState]);

  // Ao começar uma gravação manual, o campo deixa de ser o alvo ativo. Isso
  // fecha o teclado antes de revelar a barra de reconhecimento e evita que os
  // dois controles disputem a parte inferior da tela.
  useEffect(() => {
    if (manualCaptureActive) homeTextInputRef.current?.blur?.();
  }, [manualCaptureActive]);

  useEffect(() => {
    let active = true;
    getAthenaNotificationStatus().then((status) => {
      if (!active) return;
      setNotificationPermission(status.permission || 'unsupported');
      setNativePushEnabled(status.pushEnabled === true);
    }).catch(() => {
      if (active) setNotificationPermission('unsupported');
    });
    return () => { active = false; };
  }, []);

  // Native shortcuts/deep links can request only these explicit navigation
  // surfaces. Capture/voice intents are rendered as a confirmation panel
  // below; they never send a chat message or begin listening on arrival.
  useEffect(() => {
    if (!pendingIntent) return;
    switch (pendingIntent.type) {
      case ATHENA_INTENT_TYPES.OPEN_HOME:
        consumeIntent(pendingIntent.id);
        break;
      case ATHENA_INTENT_TYPES.OPEN_CHAT:
        setShowChat(true);
        consumeIntent(pendingIntent.id);
        break;
      case ATHENA_INTENT_TYPES.OPEN_PROJECT:
        navigate('/workspace/projects');
        consumeIntent(pendingIntent.id);
        break;
      case ATHENA_INTENT_TYPES.OPEN_CALENDAR:
        navigate('/workspace/calendar');
        consumeIntent(pendingIntent.id);
        break;
      case ATHENA_INTENT_TYPES.OPEN_TASK:
        navigate('/workspace/tasks');
        consumeIntent(pendingIntent.id);
        break;
      case ATHENA_INTENT_TYPES.OPEN_WORKSPACE:
        navigate('/workspace');
        consumeIntent(pendingIntent.id);
        break;
      default:
        break;
    }
  }, [consumeIntent, navigate, pendingIntent]);

  useEffect(() => {
    if (!token || notificationPermission !== 'granted') return;
    // A native install with FCM disabled must not initialise it simply because
    // the Home screen rendered. The provider only reconciles a native token
    // after the plugin reports a prior, explicit activation.
    if (IS_NATIVE_APP && !nativePushEnabled) return;
    registerAthenaNotifications(token).catch(() => {});
  }, [nativePushEnabled, notificationPermission, token]);

  const enableNotifications = useCallback(async () => {
    const permission = await requestAthenaNotificationsPermission();
    setNotificationPermission(permission);
    if (permission !== 'granted') {
      setHomeToast('A permissão de notificações não foi concedida neste aparelho.');
      return;
    }
    try {
      const result = await registerAthenaNotifications(token, { userInitiated: IS_NATIVE_APP });
      if (!result?.ok && IS_NATIVE_APP) {
        throw new Error(result?.reason || 'native_push_unavailable');
      }
      if (IS_NATIVE_APP) {
        const status = await getAthenaNotificationStatus();
        setNativePushEnabled(status.pushEnabled === true || result?.ok === true);
      } else {
        // Mantém o teste que a PWA já fazia após uma ativação explícita. O
        // Android recebe o teste apenas pelo painel de preferências, nunca
        // durante a inicialização da Home.
        await testAthenaNotifications(token);
      }
      setHomeToast('Notificações da Athena estão ativas neste aparelho.');
    } catch {
      setHomeToast('A permissão foi concedida, mas o push não pôde ser ativado. Tente novamente.');
    }
  }, [token]);

  // O shell nativo entrega o botão Voltar para a tela atual antes de navegar.
  // Assim um painel/modal é fechado primeiro, preservando o comportamento que
  // a pessoa já espera da Athena na web.
  useEffect(() => {
    const onNativeBack = (event) => {
      if (showVoiceModeMenu) setShowVoiceModeMenu(false);
      else if (showMobileMenu) setShowMobileMenu(false);
      else if (showChat) setShowChat(false);
      else if (showUpload) setShowUpload(false);
      else if (showValley) setShowValley(false);
      else if (showLocation) setShowLocation(false);
      else if (showCognitive) setShowCognitive(false);
      else if (pendingIntent) dismissIntent(pendingIntent.id);
      else if (isOverlay) setIsOverlay(false);
      else return;
      event.preventDefault();
    };
    window.addEventListener('athena-native-back', onNativeBack);
    return () => window.removeEventListener('athena-native-back', onNativeBack);
  }, [dismissIntent, isOverlay, pendingIntent, showChat, showCognitive, showLocation, showMobileMenu, showUpload, showValley, showVoiceModeMenu]);

  // Correção 04: cycle thinking thoughts during VOICE_PROCESSING
  useEffect(() => {
    if (voiceState !== VOICE_PROCESSING) { setThinkingVisible(false); return; }
    const ctx = _detectThoughtCtx(lastSentRef.current?.text || '');
    const pool = _THOUGHT_POOLS[ctx] || _THOUGHT_POOLS.generic;
    const shuffled = [...pool].sort(() => Math.random() - 0.5);
    let idx = 0;
    setThinkingThought(shuffled[0]);
    const showTimer = setTimeout(() => setThinkingVisible(true), 60);
    const iv = setInterval(() => {
      setThinkingVisible(false);
      setTimeout(() => {
        idx = (idx + 1) % shuffled.length;
        setThinkingThought(shuffled[idx]);
        setThinkingVisible(true);
      }, 350);
    }, 1000 + Math.floor(Math.random() * 500));
    return () => { clearTimeout(showTimer); clearInterval(iv); };
  }, [voiceState]);

  // Contextual notifications scheduler (while Home is open)
  useEffect(() => {
    const cleanup = startContextualScheduler({
      userName: user?.name || 'Vitor',
      getLastSummary: () => lastResponse?.slice(0, 80) || '',
    });
    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (voiceState === VOICE_PROCESSING) {
      setChatBgState('processing');
    } else if (voiceState === VOICE_SPEAKING) {
      setChatBgState('streaming');
    } else {
      setChatBgState(prev => (prev === 'streaming' || prev === 'processing') ? 'done' : 'idle');
      const t = setTimeout(() => setChatBgState('idle'), 2200);
      return () => clearTimeout(t);
    }
  }, [voiceState]);

  // Safety: se cognitive space ficou ligado mas a voz saiu de PROCESSING/SPEAKING sem TTS
  useEffect(() => {
    if (!showCognitive) return;
    if (voiceState !== VOICE_PROCESSING && voiceState !== VOICE_SPEAKING) {
      const t = setTimeout(() => setShowCognitive(false), 900);
      return () => clearTimeout(t);
    }
  }, [voiceState, showCognitive]);

  useOverlayPersistence({
    isOverlay,
    onRestore: () => setIsOverlay(false),
    isListening: voiceState === VOICE_ACTIVE,
    isSpeaking: voiceState === VOICE_SPEAKING,
    isProcessing: voiceState === VOICE_PROCESSING,
  });

  // === STOP CURRENT SPEECH (interruption) ===
  const stopSpeech = useCallback(() => {
    speechGenerationRef.current++;
    ttsAbortRef.current?.abort();
    ttsAbortRef.current = null;
    if (currentAudioRef.current) {
      try { currentAudioRef.current.pause(); currentAudioRef.current.removeAttribute('src'); currentAudioRef.current.load(); } catch {}
      currentAudioRef.current = null;
    }
    speakingLockRef.current = false;
    speechQueueRef.current = [];
  }, []);

  useEffect(() => {
    pageActiveRef.current = true;
    return () => {
      pageActiveRef.current = false;
      stopSpeech();
    };
  }, [stopSpeech]);

  // === TTS WITH GLOBAL LOCK + QUEUE ===
  const speakText = useCallback(async (text) => {
    const clean = stripEmojis(text);
    if (!clean || !pageActiveRef.current || voiceStateRef.current === VOICE_SILENT) return;

    if (speakingLockRef.current) {
      speechQueueRef.current.push(clean);
      return;
    }
    speakingLockRef.current = true;
    let nextText = clean;
    let unavailable = false;
    let playbackStarted = false;

    while (nextText && pageActiveRef.current) {
      const speechGeneration = ++speechGenerationRef.current;
      const controller = new AbortController();
      ttsAbortRef.current = controller;

      const result = await requestAthenaTts({
        api: API,
        token,
        text: nextText,
        signal: controller.signal,
      });
      if (!pageActiveRef.current || speechGenerationRef.current !== speechGeneration) return;

      if (!result?.audio) {
        unavailable = true;
        setShowCognitive(false);
      } else {
        const playback = await playAthenaAudio(result.audio, {
          signal: controller.signal,
          onCreated: (audio) => { currentAudioRef.current = audio; },
          onStart: () => {
            if (speechGenerationRef.current !== speechGeneration) return;
            playbackStarted = true;
            startSpeaking();
            setShowCognitive(false);
            window._athenaCogSignal?.('speaking', Math.min(nextText.split(/\s+/).length / 100, 1));
          },
        });
        if (playback.status !== 'finished' && playback.status !== 'cancelled') unavailable = true;
      }

      if (!pageActiveRef.current || speechGenerationRef.current !== speechGeneration) return;
      currentAudioRef.current = null;
      ttsAbortRef.current = null;
      nextText = speechQueueRef.current.shift() || null;
    }

    speakingLockRef.current = false;
    if (unavailable) setHomeToast('A resposta foi exibida, mas a voz da Athena está indisponível agora.');
    window._athenaCogSignal?.('idle');
    if (playbackStarted) doneSpeaking();
    else finishResponseWithoutSpeech();
  }, [token, startSpeaking, doneSpeaking, finishResponseWithoutSpeech]);

  // === CHAT ===
  // ── Correção 05: progressive text reveal — avoids block-dump effect ──────────
  const revealResponse = useCallback((fullText) => {
    if (typeTimerRef.current) clearTimeout(typeTimerRef.current);
    if (!fullText) { setLastResponse(''); return; }

    const words = fullText.split(' ');
    const total = words.length;

    // Short responses appear instantly (≤ 5 words)
    if (total <= 5) { setLastResponse(fullText); return; }

    // Adaptive speed: ~25-30 steps regardless of length, capped at 1.5s total
    const targetMs  = Math.min(1500, Math.max(600, total * 28));
    const numSteps  = Math.min(30, total);
    const chunkSize = Math.max(1, Math.ceil(total / numSteps));
    const delay     = Math.max(20, Math.floor(targetMs / numSteps));

    let pos = 0;
    const step = () => {
      pos = Math.min(pos + chunkSize, total);
      setLastResponse(words.slice(0, pos).join(' '));
      if (pos < total) typeTimerRef.current = setTimeout(step, delay);
    };
    step();
  }, []); // setLastResponse is a stable setter

  const sendMessage = useCallback(async (text, options = {}) => {
    if (!text?.trim()) return { accepted: false, reason: 'empty' };
    const fromVoice = options?.fromVoice === true;
    const fromQuickOverlay = options?.fromQuickOverlay === true;
    // Re-entry lock: prevent duplicate calls (active listener race, double-click, etc)
    if (processingLockRef.current) {
      setHomeToast('A Athena ainda está concluindo a resposta anterior. Sua mensagem continua no campo.');
      return { accepted: false, reason: 'processing' };
    }
    // De-dupe identical messages within 2.5s window
    const nowTs = Date.now();
    if (lastSentRef.current.text === text.trim() && nowTs - lastSentRef.current.ts < 2500) {
      setHomeToast('Essa mensagem já foi enviada há instantes. Ajuste o texto se quiser reenviar.');
      return { accepted: false, reason: 'duplicate' };
    }
    // [SessionLifecycle] Block during active restoration — queue and auto-send when ready
    if (sessionRestoringRef.current) {
      // A native overlay card cannot receive the later replay result after its
      // short-lived request ID expires. Do not secretly queue it and then ask
      // the person to retry: that would create a duplicate send.
      if (fromQuickOverlay) {
        return {
          accepted: false,
          reason: 'restoring',
          response: 'A Athena ainda está carregando sua conversa. Esta mensagem não foi enviada; aguarde alguns instantes e envie novamente.',
        };
      }
      if (pendingMessageRef.current) return { accepted: false, reason: 'restoring' };
      pendingMessageRef.current = { text: text.trim(), fromVoice };
      return { accepted: true, queued: true };
    }
    processingLockRef.current = true;
    // Só registre a deduplicação depois que a restauração terminou. Caso
    // contrário, o reenvio automático da mensagem pendente seria descartado
    // como se fosse um segundo toque do usuário.
    lastSentRef.current = { text: text.trim(), ts: nowTs };

    // Natural interruption: stop any in-progress speech when user submits
    stopSpeech();

    // Check special commands using normalized (no-accent) text
    const n = norm(text);

    // Location commands — strict patterns to avoid false positives
    const locationPatterns = [
      /\bonde\s+(eu\s+)?estou\b/, /\bme\s+localiz/, /\bmapa\s+imersivo\b/,
      /\b(mostra|mostre|mostrar|exibe|exiba|exibir|abre|abra|abrir|ver|veja|ativar?|ativa)\s+(meu\s+|minha\s+|o\s+|a\s+)?(localiza|mapa|gps)/,
      /\b(qual|cade)\s+(minha\s+|a\s+)?localiza/,
      /\bonde\s+eu\b/, /\bmeu\s+gps\b/, /\bminha\s+localizacao\b/,
      /\blocaliza(c|cao)\s+imersiva\b/,
    ];
    if (locationPatterns.some(p => p.test(n))) {
      setShowLocation(true);
      processingLockRef.current = false;
      if (fromVoice) finishResponseWithoutSpeech();
      return { accepted: true, kind: 'local_navigation' };
    }

    if (/\b(vale|valley)\s+das?\s+memorias?\b|\bminhas?\s+memorias?\b|\b(abre|abra|abrir)\s+as?\s+memorias?\b/.test(n)) {
      setShowValley(true); processingLockRef.current = false;
      if (fromVoice) finishResponseWithoutSpeech();
      return { accepted: true, kind: 'local_navigation' };
    }
    if (/\bmodo\s+foco\b|\bfocus\s+mode\b|\bmodo\s+focado\b/.test(n)) {
      setFocusMode(true);
      if (fromVoice) finishResponseWithoutSpeech();
      else if (!IS_MOBILE) startPassive();
      processingLockRef.current = false;
      return { accepted: true, kind: 'local_mode' };
    }
    if (/\bmodo\s+silencios[oa]\b|\bsilent\s+mode\b|\bmodo\s+silencio\b/.test(n)) {
      mute(); processingLockRef.current = false; return { accepted: true, kind: 'local_mode' };
    }
    if (/\b(cancela|cancel|esquece|deixa|para)\b/.test(n) && n.length < 25) {
      setFocusMode(false);
      if (fromVoice) finishResponseWithoutSpeech();
      else if (!IS_MOBILE) startPassive(); else setIdle();
      processingLockRef.current = false;
      return { accepted: true, kind: 'local_mode' };
    }
    if (/\bmodo\s+normal\b|\bvoltar\s+ao\s+normal\b|\bsai\s+do\s+foco\b|\bpode\s+falar\b/.test(n)) {
      setFocusMode(false);
      if (fromVoice) finishResponseWithoutSpeech();
      else if (!IS_MOBILE) startPassive(); else setIdle();
      processingLockRef.current = false;
      return { accepted: true, kind: 'local_mode' };
    }

    // A new real request replaces the visual aid from the previous turn.
    // Rejected taps and local navigation must leave the existing answer on
    // screen rather than making it appear that Athena went silent.
    setLastGeneratedImage(null);

    try {
      // Ações operacionais passam sempre pelo mesmo orquestrador. Um texto do
      // modelo não é usado como evidência de alarme, agenda, tarefa ou arquivo.
      // Esta etapa precisa ficar no mesmo boundary do chat: uma falha local
      // jamais pode manter o lock ligado sem retornar um estado visível.
      const directIntent = detectAthenaOperationalIntent(text, { conversation: messages });
      const pendingInput = (!directIntent && pendingCalendarRequestRef.current)
        ? `${pendingCalendarRequestRef.current} às ${text}`
        : (!directIntent && pendingTaskRequestRef.current ? `${pendingTaskRequestRef.current} ${text}` : text);
      const operationalActionResult = await executeAthenaOperationalAction({
        message: pendingInput,
        apiBase: API,
        token,
        sessionId: sessionIdRef.current,
        conversation: messages,
      });
      if (operationalActionResult) {
        if (operationalActionResult.type === 'CREATE_CALENDAR_EVENT') {
          pendingCalendarRequestRef.current = operationalActionResult.status === 'needs_input' ? pendingInput : '';
        }
        if (operationalActionResult.type === 'CREATE_TASK') {
          pendingTaskRequestRef.current = operationalActionResult.status === 'needs_input' ? pendingInput : '';
        }
        const answer = operationalActionResult.userMessage;
        const actionMessages = [{ role: 'user', content: text }, {
          role: 'assistant',
          content: answer,
          response_presentation: operationalActionResult.responsePresentation || null,
          response_message_id: operationalActionResult.responseMessageId || null,
          card_id: operationalActionResult.responseMessageId || null,
          session_id: operationalActionResult.sessionId || sessionIdRef.current || null,
          action_result: operationalActionResult,
          attachments: operationalActionResult.pendingAttachments || undefined,
        }];
        if (operationalActionResult.success && operationalActionResult.imageUrl) {
          actionMessages.push({
            role: 'assistant', type: 'visual_aid', content: '',
            imageUrl: operationalActionResult.imageUrl,
            prompt: operationalActionResult.promptUsed || text,
            provider: operationalActionResult.provider,
            image: operationalActionResult.image || null,
          });
          setLastGeneratedImage({
            imageUrl: operationalActionResult.imageUrl,
            prompt: operationalActionResult.promptUsed || text,
            provider: operationalActionResult.provider,
          });
        }
        setMessages(prev => [...prev, ...actionMessages]);
        if (operationalActionResult.sessionId) {
          setSessionId(operationalActionResult.sessionId);
          sessionIdRef.current = operationalActionResult.sessionId;
          localStorage.setItem(ACTIVE_SESSION_STORAGE_KEY, operationalActionResult.sessionId);
        }
        if (operationalActionResult.pendingAttachments?.length) setHomeToast('A foto está pronta para ser anexada à conversa.');
        setLastResponseIsError(false);
        setLastFailedMessage('');
        revealResponse(answer);
        processingLockRef.current = false;
        if (fromQuickOverlay) finishResponseWithoutSpeech();
        else await speakText(answer);
        return { accepted: true, kind: 'action', response: answer };
      }

      cogTypeRef.current = _detectThoughtCtx(text);
      // Alterna o vídeo de fundo a cada novo ciclo de raciocínio.
      setThinkingVideoIndex(nextThinkingVideoRef.current);
      nextThinkingVideoRef.current = (nextThinkingVideoRef.current + 1) % 2;
      setShowCognitive(true);
      setMessages(prev => [...prev, { role: 'user', content: text }]);
      setProcessing();
      window._athenaCogSignal?.('processing', Math.min(text.trim().split(/\s+/).length / 25, 1));

      const screenImage = captureScreen();
      // Device state is opt-in and is checked only for an explicit question
      // about battery, network or audio route. The helper submits a reduced
      // snapshot separately and returns only a transient proof reference.
      let deviceContext = null;
      try { deviceContext = await prepareChatContext(text); } catch { /* Context must never block chat. */ }
      const { data } = await axios.post(`${API}/chat/message`, {
        message: text,
        session_id: sessionIdRef.current,
        // O relógio do dispositivo é a fonte correta para perguntas como
        // "que horas são?", inclusive no servidor global do Cloudflare.
        client_time: new Date().toISOString(),
        client_time_display: new Intl.DateTimeFormat('pt-BR', {
          dateStyle: 'full', timeStyle: 'medium',
        }).format(new Date()),
        client_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Fortaleza',
        mode: 'athena2',
        screen_image: screenImage || undefined,
        social_prefs: loadSocialPrefs(),
        ...(deviceContext ? { device_context: deviceContext } : {}),
      }, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true, timeout: 30_000 });
      if (data?.is_error || !String(data?.response || '').trim()) {
        const responseError = new Error(data?.error_code || 'ATHENA_EMPTY_RESPONSE');
        responseError.response = { status: data?.is_error ? 503 : 502, data };
        throw responseError;
      }
      const rawResponse = data.response || "";
      const rawCleaned = rawResponse.replace(/<action>[\s\S]*?<\/action>/g, "").trim();
      // Tags emitted by the language model are never execution evidence. A real
      // reminder/notification must pass through AthenaActionOrchestrator and
      // return a verified ActionResult before it can be presented as created.
      const { notifBody: _nb, cleanText: responseText } = parseNotifFromResponse(rawCleaned);
      const safeResponseText = _nb
        ? 'Posso criar esse lembrete de verdade para você. Diga o horário e o que devo lembrar, e eu confirmo somente depois que ele estiver registrado.'
        : responseText;
      const actionResult = await detectAndExecuteAction(text, rawResponse);
      const spokenText = actionResult?.spokenText || safeResponseText;
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: spokenText,
        // O envelope é opcional; ramos locais e mensagens históricas continuam
        // como texto comum no AdaptiveResponseRenderer.
        response_presentation: data.response_presentation || null,
        response_message_id: data.response_message_id || null,
        card_id: data.card_id || null,
        session_id: data.session_id || sessionIdRef.current || null,
      }]);
      setLastResponseIsError(false);
      setLastFailedMessage('');
      revealResponse(spokenText);   // Correção 05: progressive reveal
      if (data.session_id) {
        setSessionId(data.session_id);
        sessionIdRef.current = data.session_id;
        localStorage.setItem(ACTIVE_SESSION_STORAGE_KEY, data.session_id);
      }
      processingLockRef.current = false;
      // Camada 6: usar voice_text limpo do backend (sem markdown/emojis/símbolos)
      // voice_text=null é sinal de silêncio intencional (ex: is_error). Não falar erro.
      if (!data.is_error) {
        if (fromQuickOverlay) {
          finishResponseWithoutSpeech();
          return { accepted: true, kind: 'chat', response: spokenText };
        }
        const ttsText = actionResult?.spokenText ? spokenText : (data.voice_text || spokenText);
        await speakText(ttsText);
      }
      // Restart de voz tratado pelo engine via doneSpeaking() dentro de speakText
      return { accepted: true, kind: 'chat', response: spokenText };
    } catch (error) {
      const failure = normalizeAthenaApiFailure(error);
      processingLockRef.current = false;
      // A tentativa falhou e já foi apresentada à pessoa. Ela deve poder
      // reenviar o mesmo texto imediatamente, sem cair no de-dupe local.
      if (lastSentRef.current.text === text.trim()) {
        lastSentRef.current = { text: '', ts: 0 };
      }
      window._athenaCogSignal?.('idle');
      setShowCognitive(false);
      if (isAthenaSessionFailure(failure)) {
        signalAthenaAuthExpired({ token });
        navigate('/login', { replace: true });
        return {
          accepted: false,
          reason: 'session_expired',
          response: 'Sua sessão expirou. Abra a Athena para entrar novamente.',
        };
      }
      setMessages(prev => [...prev, { role: 'assistant', content: failure.userMessage }]);
      setLastResponseIsError(true);
      setLastFailedMessage(text.trim());
      revealResponse(failure.userMessage);
      if (!IS_MOBILE) startPassive(); else setIdle();
      return {
        accepted: false,
        reason: 'request_failed',
        failureCode: failure.code,
        response: failure.userMessage,
      };
    }
  }, [token, speakText, stopSpeech, prepareChatContext, messages, navigate, revealResponse, finishResponseWithoutSpeech]);

  useEffect(() => { sendMessageRef.current = sendMessage; }, [sendMessage]);

  // NativeAppShell owns the one Capacitor listener. This page only lends its
  // existing authenticated pipeline while it is mounted; no message or result
  // is copied to storage or a native preference.
  useEffect(() => registerAthenaQuickOverlayMessageHandler(async ({ text }) => {
    const send = sendMessageRef.current;
    if (typeof send !== 'function') {
      return { accepted: false, response: 'Abra a Athena e tente novamente. A conversa ainda não está pronta.' };
    }
    const result = await send(text, { fromQuickOverlay: true });
    if (result?.queued === true) {
      return { accepted: false, queued: true, response: '' };
    }
    if (result?.kind === 'local_navigation' || result?.kind === 'local_mode') {
      return {
        accepted: false,
        response: 'Esse pedido precisa ser concluído na tela da Athena. Toque em ↗ para abri-la.',
      };
    }
    return result;
  }), []);

  // === PASSIVE WAKE WORD LISTENING (desktop only) ===

  const detectAndExecuteAction = useCallback(async (_text, preResponse) => {
    // Operational actions are handled before the model through
    // AthenaActionOrchestrator. This post-processing step only removes legacy
    // tags from text; it never calls removed /execute-action endpoints.
    const clean = String(preResponse || '').replace(/<action>[\s\S]*?<\/action>/g, '').trim();
    return clean ? { detected: true, spokenText: clean } : null;
  }, []);


  // Start passive on mount (desktop only) + ambient messages + GPS WATCH + RESUME LAST SESSION
  useEffect(() => {
    const t = IS_MOBILE || !continuousMode ? null : setTimeout(() => startPassive(), 1500);

    // The restoration callback runs outside the normal submit handler. Replay
    // immediately (instead of a later timer) so another tap/listener cannot
    // take the processing lock between restoration and the queued message.
    // The draft is restored if the replay is rejected for any reason.
    const replayQueuedMessage = (pendingMessage) => {
      const queued = typeof pendingMessage === 'string'
        ? { text: pendingMessage, fromVoice: false }
        : pendingMessage;
      const message = String(queued?.text || '').trim();
      if (!message) return;
      const send = sendMessageRef.current;
      if (!send) {
        pendingMessageRef.current = queued;
        return;
      }
      // Keep the normal composer clean while the request begins. If the
      // request cannot start, the original text returns without overwriting a
      // newer draft the person may have typed meanwhile.
      setTextInput('');
      void Promise.resolve(send(message, { fromVoice: queued?.fromVoice === true })).then((outcome) => {
        if (outcome?.accepted !== false) return;
        if (queued?.fromVoice) finishResponseWithoutSpeech();
        setTextInput((current) => String(current || '').trim() ? current : message);
      }).catch(() => {
        if (queued?.fromVoice) finishResponseWithoutSpeech();
        setTextInput((current) => String(current || '').trim() ? current : message);
      });
    };

    // === RESUME LAST CONVERSATION ===
    // [SessionLifecycle] Generation counter prevents stale restoration from overwriting active session
    const _gen = ++restoreGenerationRef.current;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/memory/sessions`, {
          headers: { Authorization: `Bearer ${token}` }, withCredentials: true, timeout: 15_000,
        });
        if (_gen !== restoreGenerationRef.current) return;
        const sessions = data?.sessions || [];
        const activeSessionId = localStorage.getItem(ACTIVE_SESSION_STORAGE_KEY);
        const recent = sessions.find(s => s.session_id === activeSessionId) || sessions[0];
        if (recent?.session_id && !sessionIdRef.current) {
          const { data: full } = await axios.get(`${API}/memory/sessions/${recent.session_id}`, {
            headers: { Authorization: `Bearer ${token}` }, withCredentials: true, timeout: 15_000,
          });
          if (_gen !== restoreGenerationRef.current || sessionIdRef.current) return;
          // Set session atomically via ref THEN state — no null window
          sessionIdRef.current = recent.session_id;
          setSessionId(recent.session_id);
          localStorage.setItem(ACTIVE_SESSION_STORAGE_KEY, recent.session_id);
          const msgs = full?.messages || [];
          setMessages(msgs);
          const lastAssistant = [...msgs].reverse().find(m => m.role === 'assistant');
          if (lastAssistant) {
            setLastResponse(lastAssistant.content);
            setLastResponseIsError(false);
            setLastFailedMessage('');
          }

          // Unblock sendMessage now — before reentry POST (which adds extra latency)
          sessionRestoringRef.current = false;
          setSessionRestoring(false);
          setSessionReady(true);
          console.log('[SessionLifecycle] restoration done, sessionId:', sessionIdRef.current);

          const _pending = pendingMessageRef.current;
          if (_pending) {
            pendingMessageRef.current = null;
            reentryDoneRef.current = true; // skip reentry — user already has a message
            replayQueuedMessage(_pending);
            return;
          }

          if (msgs.length >= 2 && !reentryDoneRef.current) {
            reentryDoneRef.current = true;
            try {
              const { data: rd } = await axios.post(`${API}/chat/reentry`,
                { session_id: recent.session_id },
                { headers: { Authorization: `Bearer ${token}` }, withCredentials: true, timeout: 15_000 }
              );
              if (_gen !== restoreGenerationRef.current || sessionIdRef.current !== recent.session_id) return;
              if (rd?.message) {
                setMessages(prev => [...prev, { role: 'assistant', content: rd.message }]);
                setLastResponse(rd.message);
              }
            } catch {}
          }
          return;
        }
      } catch (error) {
        const failure = normalizeAthenaApiFailure(error);
        if (isAthenaSessionFailure(failure)) {
          signalAthenaAuthExpired({ token });
          navigate('/login', { replace: true });
        }
      }
      // No session found or error — unblock anyway
      if (_gen === restoreGenerationRef.current) {
        sessionRestoringRef.current = false;
        setSessionRestoring(false);
        console.log('[SessionLifecycle] restoration done (no session)');
        const _pending = pendingMessageRef.current;
        if (_pending) {
          pendingMessageRef.current = null;
          replayQueuedMessage(_pending);
        }
      }
    })();

    // Fase 5 privacy boundary: Home no longer starts a high-accuracy GPS
    // watch on mount. The existing location sheet asks for a one-time local
    // position only after the person explicitly opens and requests it.

    const requestAmbientPresence = async () => {
      if (ambientInFlightRef.current) return;
      if (voiceStateRef.current === VOICE_SPEAKING || voiceStateRef.current === VOICE_PROCESSING || voiceStateRef.current === VOICE_ACTIVE) return;
      if (voiceStateRef.current === VOICE_SILENT || voiceStateRef.current === VOICE_FOCUS) return;
      ambientInFlightRef.current = true;
      try {
        const ambientSessionId = sessionIdRef.current;
        const instant = new Date();
        const r = await axios.post(`${API}/ambient-message`, {
          session_id: ambientSessionId,
          client_time: instant.toISOString(),
          client_time_display: new Intl.DateTimeFormat('pt-BR', { dateStyle: 'full', timeStyle: 'short' }).format(instant),
          client_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Fortaleza',
        }, {
          headers: { Authorization: `Bearer ${token}` }, withCredentials: true, timeout: 10000,
        });
        if (ambientSessionId !== sessionIdRef.current) return;
        if (r.data.should_speak && r.data.message) {
          setMessages(prev => [...prev, { role: 'assistant', content: r.data.message }]);
          setLastResponse(r.data.message);
          if (voiceStateRef.current === VOICE_PASSIVE) {
            await speakText(r.data.message);
          }
        }
      } catch {
        // Presence é opcional: uma indisponibilidade nunca bloqueia a Home.
      } finally { ambientInFlightRef.current = false; }
    };

    // A primeira avaliação acontece depois da restauração assíncrona da sessão.
    // Não bloqueia a tela e recua poucas vezes caso a rede ainda esteja carregando.
    let bootAttempts = 0;
    const bootAmbient = () => {
      if (ambientBootDoneRef.current) return;
      if (sessionRestoringRef.current && bootAttempts++ < 6) {
        bootTimer = setTimeout(bootAmbient, 600);
        return;
      }
      ambientBootDoneRef.current = true;
      requestAmbientPresence();
    };
    let bootTimer = setTimeout(bootAmbient, 900);
    const ambientInterval = setInterval(requestAmbientPresence, 5 * 60 * 1000);

    return () => {
      if (t) clearTimeout(t);
      clearTimeout(bootTimer);
      clearInterval(ambientInterval);
      stopSpeech();
    };
  }, []);

  useEffect(() => {
    const handleVisibility = () => {
      // Ao voltar ao app, retome a escuta contínua apenas quando não há uma
      // resposta em andamento. Antes isso criava um segundo listener durante
      // o áudio da Athena e deixava o modo contínuo instável no celular.
      if (!document.hidden && ![VOICE_SPEAKING, VOICE_PROCESSING].includes(voiceStateRef.current)) {
        if (continuousModeRef.current) startActive();
        // in manual mode, do not auto-start on visibility change
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [startActive]);

  // Text submit
  const handleTextSubmit = (e) => {
    e.preventDefault();
    const submittedText = textInput.trim();
    if (!submittedText) return;

    const keepDuringRestore = sessionRestoringRef.current;
    const outcome = sendMessage(submittedText);
    // [SessionLifecycle] Preserve text in input during restoration (auto-clears when message is sent).
    // A rejected lock, de-dupe, or service failure restores the draft instead
    // of silently discarding what the person just typed. Session expiration is
    // the only exception because the login transition owns that recovery.
    if (!keepDuringRestore) setTextInput('');
    void Promise.resolve(outcome).then((result) => {
      if (result?.accepted !== false || result?.reason === 'session_expired') return;
      setTextInput((current) => current.trim() ? current : submittedText);
    }).catch(() => {
      setTextInput((current) => current.trim() ? current : submittedText);
      setHomeToast('Não consegui iniciar essa mensagem. Ela continua no campo para você tentar novamente.');
    });
  };

  // Shares from Android remain separate from the model prompt. A person has
  // to choose this action before text reaches the editable draft, and files
  // are only handled in Workspace where their upload/attachment flow lives.
  const handleUseSharedText = useCallback((item) => {
    const sharedText = typeof item?.text === 'string' ? item.text.trim() : '';
    if (!sharedText) {
      setHomeToast('Esse conteúdo não possui texto para adicionar ao rascunho.');
      return false;
    }

    setTextInput((previous) => (previous.trim()
      ? `${previous.trimEnd()}\n${sharedText}`
      : sharedText));

    // A share that also includes files must remain visible so its attachments
    // can be intentionally opened in Workspace. Text-only shares can leave
    // the inbox after being copied into the draft.
    if (!Array.isArray(item?.attachments) || item.attachments.length === 0) {
      void consumePendingDeviceContext(item.id);
    }
    setHomeToast('Texto compartilhado adicionado ao rascunho. Revise antes de enviar.');
    return true;
  }, [consumePendingDeviceContext]);

  const handleDiscardSharedContext = useCallback((item) => {
    if (!item?.id) return;
    void removePendingDeviceContext(item.id);
  }, [removePendingDeviceContext]);

  // Interrupt speaking
  const handleInterrupt = () => {
    stopSpeech();
    // A interrupção é uma tomada de turno do usuário: não volte para espera
    // passiva e não descarte a janela do microfone já preparada pelo V2.
    if (continuousMode) beginBargeIn();
    else if (!IS_MOBILE) startPassive();
    else setIdle();
  };

  // A Workspace é deliberadamente silenciosa. Sempre encerra a conversa da
  // Home antes da troca de rota, inclusive TTS que ainda esteja sendo gerado.
  const openWorkspace = useCallback(() => {
    stop();
    stopSpeech();
    navigate('/workspace');
  }, [navigate, stop, stopSpeech]);

  const handleOpenSharedAttachments = useCallback(() => {
    setHomeToast('Abra o conteúdo na Workspace para revisar os arquivos antes de anexar.');
    openWorkspace();
  }, [openWorkspace]);

  const selectVoiceMode = useCallback((mode) => {
    if (mode !== 'manual') {
      setHomeToast('O modo contínuo está em preparação. Por enquanto, use a gravação manual.');
      return;
    }
    setVoiceMode(mode);
    setShowVoiceModeMenu(false);
    if (mode === 'manual') {
      setContinuousMode(false);
      continuousModeRef.current = false;
      stop();
    } else {
      setContinuousMode(true);
      continuousModeRef.current = true;
      startActive();
    }
  }, [setContinuousMode, startActive, stop]);

  // Controles explícitos de voz. A Home tinha deixado os modos escondidos no
  // menu móvel e o núcleo recebia pointer/touch/click ao mesmo tempo. Este é
  // o único caminho oficial para iniciar e finalizar a fala: um botão nativo
  // com um único `click`, estável em desktop, Chrome Android e PWA instalada.
  const handleVoiceButton = useCallback(() => {
    if (sessionRestoringRef.current) {
      setHomeToast('Aguarde a conversa terminar de ser restaurada.');
      return;
    }
    if (voiceStateRef.current === VOICE_SPEAKING) {
      // Barge-in é um turno explícito do usuário: interrompe apenas o áudio
      // oficial e abre uma nova captura no modo escolhido.
      stopSpeech();
      cancelSpeaking();
      if (voiceMode === 'manual') startManual();
      else startActive();
      return;
    }
    if (voiceMode === 'manual') {
      playManualVoiceCue(voiceStateRef.current === VOICE_ACTIVE ? 'finish' : 'start');
      toggleManual();
      return;
    }
    if (voiceStateRef.current === VOICE_ACTIVE) finishActive();
    else startActive();
  }, [voiceMode, toggleManual, finishActive, startActive, startManual, stopSpeech, cancelSpeaking]);

  const clearCoreMenuHold = useCallback(() => {
    if (coreMenuHoldRef.current) {
      window.clearTimeout(coreMenuHoldRef.current);
      coreMenuHoldRef.current = null;
    }
  }, []);

  const startCoreMenuHold = useCallback(() => {
    clearCoreMenuHold();
    // A recording/response turn keeps its normal tap-to-stop behavior. The
    // radial menu is for the calm core state, not a hidden interruption path.
    if ([VOICE_ACTIVE, VOICE_PROCESSING, VOICE_SPEAKING].includes(voiceStateRef.current)) return;
    coreMenuTriggeredRef.current = false;
    coreMenuHoldRef.current = window.setTimeout(() => {
      coreMenuHoldRef.current = null;
      coreMenuTriggeredRef.current = true;
      setCoreMenuOpen(true);
    }, 560);
  }, [clearCoreMenuHold]);

  const handleCoreOrbClick = useCallback(() => {
    if (coreMenuTriggeredRef.current) {
      coreMenuTriggeredRef.current = false;
      return;
    }
    handleVoiceButton();
  }, [handleVoiceButton]);

  const handleCoreMenuAction = useCallback((action) => {
    setCoreMenuOpen(false);
    if (action === 'chat') {
      setShowChat(true);
      return;
    }
    const routes = {
      today: '/workspace/today',
      calendar: '/workspace/calendar',
      routine: '/workspace/today',
      tasks: '/workspace/tasks',
      capture: '/workspace/inbox',
      projects: '/workspace/projects',
      vault: '/workspace/vault',
    };
    if (!routes[action]) return;
    stop();
    stopSpeech();
    navigate(routes[action]);
  }, [navigate, stop, stopSpeech]);

  useEffect(() => () => clearCoreMenuHold(), [clearCoreMenuHold]);

  const handleQuickAthenaSubmit = useCallback(async ({ intent, text }) => {
    // The compact surface never gets a second chat/session implementation.
    // Its explicit submit enters the exact Home pathway, including the
    // ActionOrchestrator verification and normal conversation persistence.
    const message = quickAthenaMessage(intent, text);
    if (!message) return { accepted: false, reason: 'empty' };
    return sendMessage(message);
  }, [sendMessage]);

  const closeQuickAthena = useCallback((intent) => {
    // A hidden compact card must never leave the microphone listening.
    if (voiceStateRef.current === VOICE_ACTIVE) {
      stop();
      setIdle();
    }
    dismissIntent(intent?.id);
  }, [dismissIntent, setIdle, stop]);

  const expandQuickAthena = useCallback((intent) => {
    closeQuickAthena(intent);
    // The session and messages remain owned by Home; expanding only reveals
    // the existing conversation view rather than opening a second chat.
    setShowChat(true);
  }, [closeQuickAthena]);

  // ── Orb hold gesture ─────────────────────────────────────────────────────────
  // DOUBLE_TAP_MS: max gap between first and second tap to count as double-tap
  // WAKE_HOLD_MS:  hold time for voice activation (1 s — official standard)
  // NAV_HOLD_MS:   hold time for navigation mode (preserved separately)
  const DOUBLE_TAP_MS = 450;
  const WAKE_HOLD_MS  = 1000;   // 1 s: voice activation
  const NAV_HOLD_MS   = 1500;   // 1.5 s: drag navigation mode

  // wakeActivateRef always holds the freshest activation logic (no stale closure).
  // Updated inline on every render — reading it inside the RAF tick is always safe.
  wakeActivateRef.current = () => {
    stopSpeech();
    // O modo contínuo foi retirado temporariamente da Home. Mesmo um gesto
    // legado não pode religá-lo enquanto refinamos a captura manual nativa.
    setContinuousMode(false);
    continuousModeRef.current = false;
    setVoiceMode('manual');
    if (voiceStateRef.current !== VOICE_ACTIVE) startManual();
  };

  const cancelWake = useCallback(() => {
    if (wakeRafRef.current) { cancelAnimationFrame(wakeRafRef.current); wakeRafRef.current = null; }
    holdingRef.current = false;
    setWakeActive(false);
    setWakeProgress(0);
  }, []);

  const startWake = useCallback(() => {
    holdingRef.current = true;
    setWakeActive(true);
    wakeStartRef.current = performance.now();
    const tick = (now) => {
      if (!holdingRef.current) return;
      const progress = Math.min((now - wakeStartRef.current) / WAKE_HOLD_MS, 1);
      setWakeProgress(progress);
      if (progress < 1) {
        wakeRafRef.current = requestAnimationFrame(tick);
      } else {
        holdingRef.current = false;
        setWakeActive(false);
        setWakeProgress(0);
        try { navigator.vibrate?.(40); } catch {}
        wakeActivateRef.current?.();
      }
    };
    wakeRafRef.current = requestAnimationFrame(tick);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Phase 2: double-tap + hold 1.5s → nav mode; pinch to scale; free drag → navigate ─
  // left = /visual-call | right = /workspace
  const DRAG_THRESHOLD = IS_MOBILE ? 90 : 150;
  const MIN_SCALE      = 0.35;
  const MAX_SCALE      = 1.4;

  // Applies position + scale directly to the orb container DOM node — zero re-renders
  const applyOrbTransform = useCallback((x, y, scale) => {
    if (orbContainerRef.current)
      orbContainerRef.current.style.transform = `translate3d(${x}px,${y}px,0) scale(${scale})`;
  }, []);

  // Clamp so the orb stays within the viewport regardless of scale
  const clampPos = useCallback((x, y, scale) => {
    const margin = 55 * scale;
    const maxX = Math.max(80, window.innerWidth  / 2 - margin);
    const maxY = Math.max(60, window.innerHeight / 2 - margin);
    return { x: Math.max(-maxX, Math.min(maxX, x)), y: Math.max(-maxY, Math.min(maxY, y)) };
  }, []);

  const cancelDrag = useCallback(() => {
    if (dragHoldTimerRef.current) { clearTimeout(dragHoldTimerRef.current); dragHoldTimerRef.current = null; }
    isDraggingRef.current    = false;
    dragTargetRef.current    = null;
    primaryPointerRef.current = null;
    pinchStartDistRef.current = 0;
    setDragReady(false);
    setDragTarget(null);
    // Spring-back via temporary CSS transition applied directly to DOM
    if (orbContainerRef.current) {
      orbContainerRef.current.style.transition = 'transform 0.45s cubic-bezier(0.34,1.56,0.64,1)';
      orbContainerRef.current.style.transform  = 'translate3d(0,0,0) scale(1)';
      setTimeout(() => { if (orbContainerRef.current) orbContainerRef.current.style.transition = ''; }, 460);
    }
    dragPosRef.current   = { x: 0, y: 0 };
    dragScaleRef.current = 1;
  }, []);

  const startNavHold = useCallback((e) => {
    cancelWake();
    cancelDrag();
    dragStartXRef.current = e.clientX;
    dragStartYRef.current = e.clientY;
    dragPosRef.current    = { x: 0, y: 0 };
    dragScaleRef.current  = 1;
    holdingRef.current    = true;
    setWakeMode('nav');
    setWakeActive(true);
    wakeStartRef.current  = performance.now();
    const tick = (now) => {
      if (!holdingRef.current) return;
      const progress = Math.min((now - wakeStartRef.current) / NAV_HOLD_MS, 1);
      setWakeProgress(progress);
      if (progress < 1) {
        wakeRafRef.current = requestAnimationFrame(tick);
      } else {
        holdingRef.current = false;
        setWakeActive(false);
        setWakeProgress(0);
        try { navigator.vibrate?.(30); } catch {}
        isDraggingRef.current = true;
        dragTargetRef.current = null;
        if (orbContainerRef.current) {
          orbContainerRef.current.style.transition = '';
          orbContainerRef.current.style.transform  = 'translate3d(0,0,0) scale(1)';
        }
        setDragReady(true);
      }
    };
    wakeRafRef.current = requestAnimationFrame(tick);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cancelWake, cancelDrag]);

  // Single-finger drag — direct DOM, no setState per frame
  const handleOrbPointerMove = useCallback((e) => {
    if (!isDraggingRef.current) return;
    if (pinchStartDistRef.current > 0) return; // let touch handle pinch
    if (primaryPointerRef.current !== null && e.pointerId !== primaryPointerRef.current) return;
    e.preventDefault();
    const deltaX = e.clientX - dragStartXRef.current;
    const deltaY = e.clientY - dragStartYRef.current;
    const clamped = clampPos(deltaX, deltaY, dragScaleRef.current);
    dragPosRef.current = clamped;
    applyOrbTransform(clamped.x, clamped.y, dragScaleRef.current);
    // Only call setState when crossing threshold (not every frame)
    const newTarget = Math.abs(clamped.x) >= DRAG_THRESHOLD
      ? (clamped.x < 0 ? 'visual-call' : 'workspace') : null;
    if (newTarget !== dragTargetRef.current) {
      dragTargetRef.current = newTarget;
      setDragTarget(newTarget);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyOrbTransform, clampPos]);

  // Two-finger pinch-to-scale — touch events only, no pointer interference
  const handleOrbTouchMove = useCallback((e) => {
    if (!isDraggingRef.current || e.touches.length !== 2) {
      if (e.touches.length < 2) pinchStartDistRef.current = 0;
      return;
    }
    e.preventDefault();
    const t1 = e.touches[0], t2 = e.touches[1];
    const dist = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
    const midX = (t1.clientX + t2.clientX) / 2;
    const midY = (t1.clientY + t2.clientY) / 2;
    if (!pinchStartDistRef.current) {
      // Init — anchor start position to current orb position
      pinchStartDistRef.current  = dist;
      pinchStartScaleRef.current = dragScaleRef.current;
      dragStartXRef.current = midX - dragPosRef.current.x;
      dragStartYRef.current = midY - dragPosRef.current.y;
      return;
    }
    const newScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE,
      pinchStartScaleRef.current * (dist / pinchStartDistRef.current)));
    dragScaleRef.current = newScale;
    const rawX = midX - dragStartXRef.current;
    const rawY = midY - dragStartYRef.current;
    const clamped = clampPos(rawX, rawY, newScale);
    dragPosRef.current = clamped;
    applyOrbTransform(clamped.x, clamped.y, newScale);
    const newTarget = Math.abs(clamped.x) >= DRAG_THRESHOLD
      ? (clamped.x < 0 ? 'visual-call' : 'workspace') : null;
    if (newTarget !== dragTargetRef.current) { dragTargetRef.current = newTarget; setDragTarget(newTarget); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyOrbTransform, clampPos]);

  const handleOrbActivate = useCallback((e) => {
    if (voiceMode === 'manual' || voiceMode === 'continuous') {
      e.preventDefault();
      const now = Date.now();
      if (now - lastOrbActivationRef.current < 650) return;
      lastOrbActivationRef.current = now;
      // Um toque no núcleo nunca interrompe uma resposta em andamento.
      // No manual o próprio motor consulta seu estado interno, que é síncrono;
      // nunca dependemos de um re-render para reconhecer o segundo toque.
      if (voiceMode === 'manual') toggleManual();
      else if (voiceStateRef.current === VOICE_ACTIVE) finishActive();
      else startActive();
      return;
    }
    // Secondary finger: skip gesture logic (touch events handle pinch)
    if (primaryPointerRef.current !== null && e.pointerId !== primaryPointerRef.current) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    primaryPointerRef.current = e.pointerId;
    const now = Date.now();
    const since = now - lastTapAtRef.current;
    if (since < DOUBLE_TAP_MS && since > 40) {
      lastTapAtRef.current = 0;
      cancelWake();
      startNavHold(e);
    } else {
      lastTapAtRef.current = now;
      cancelDrag();
      startWake();
    }
  }, [voiceMode, toggleManual, finishActive, startActive, startWake, cancelWake, startNavHold, cancelDrag]);

  const handleOrbPointerDown = useCallback((e) => handleOrbActivate(e), [handleOrbActivate]);
  const handleOrbTouchEndFallback = useCallback((e) => handleOrbActivate(e), [handleOrbActivate]);

  const handleOrbPointerUp = useCallback((e) => {
    if (voiceMode === 'manual' || voiceMode === 'continuous') return;
    pinchStartDistRef.current = 0; // reset pinch on any pointer release
    if (e.pointerId !== primaryPointerRef.current) return;
    primaryPointerRef.current = null;
    const target = dragTargetRef.current;
    cancelWake();
    cancelDrag();
    if (target === 'visual-call') navigate('/visual-call');
    else if (target === 'workspace') openWorkspace();
  }, [voiceMode, cancelWake, cancelDrag, navigate, openWorkspace]);

  // Double-tap background to cancel nav mode
  const handleBgPointerDown = useCallback((e) => {
    const now = Date.now();
    const since = now - lastBgTapRef.current;
    if (since < DOUBLE_TAP_MS && since > 40) {
      lastBgTapRef.current = 0;
      cancelDrag();
    } else {
      lastBgTapRef.current = now;
    }
  }, [cancelDrag]);

  // Esc key cancels nav mode on desktop
  useEffect(() => {
    const onEsc = (e) => { if (e.key === 'Escape' && isDraggingRef.current) cancelDrag(); };
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [cancelDrag]);

  // Touch activate - long press nucleus toggles continuous conversation mode
  const handleTouchActivate = (action = 'hold-start') => {
    stopSpeech();

    if (action === 'hold-start') {
      setContinuousMode(false);
      continuousModeRef.current = false;
      setVoiceMode('manual');
      if (voiceStateRef.current !== VOICE_ACTIVE) startManual();

      return;
    }

    if (action === 'hold-end') {
      return;
    }
  };

  // Session management
  const newConversation = async () => {
    try {
      restoreGenerationRef.current++;
      const { data } = await axios.post(`${API}/memory/sessions/new`, {},
        { headers: { Authorization: `Bearer ${token}` }, withCredentials: true });
      sessionIdRef.current = data.session_id;
      setSessionId(data.session_id);
      localStorage.setItem(ACTIVE_SESSION_STORAGE_KEY, data.session_id);
      sessionRestoringRef.current = false;
      setSessionRestoring(false);
      setSessionReady(true);
      setMessages([]);
      setLastResponse('');
    } catch {}
  };

  const loadSession = async (sid) => {
    try {
      const _gen = ++restoreGenerationRef.current;
      const { data } = await axios.get(`${API}/memory/sessions/${sid}`,
        { headers: { Authorization: `Bearer ${token}` }, withCredentials: true });
      if (_gen !== restoreGenerationRef.current) return;
      sessionIdRef.current = sid;
      setSessionId(sid);
      localStorage.setItem(ACTIVE_SESSION_STORAGE_KEY, sid);
      sessionRestoringRef.current = false;
      setSessionRestoring(false);
      setSessionReady(true);
      const msgs = data.messages || [];
      setMessages(msgs);
      setLastResponse(msgs.filter(m => m.role === 'assistant').pop()?.content || '');
      if (msgs.length >= 2) {
        const { data: rd } = await axios.post(`${API}/chat/reentry`, { session_id: sid },
          { headers: { Authorization: `Bearer ${token}` }, withCredentials: true });
        if (_gen !== restoreGenerationRef.current || sessionIdRef.current !== sid) return;
        if (rd?.message) {
          setMessages(prev => [...prev, { role: 'assistant', content: rd.message }]);
          setLastResponse(rd.message);
        }
      }
    } catch {}
  };

  // Overlay
  const toggleScreen = useCallback(async () => {
    if (screenActive) {
      screenStreamRef.current?.getTracks().forEach(t => t.stop());
      screenStreamRef.current = null;
      setScreenActive(false);
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({ video: true });
      screenStreamRef.current = stream;
      screenVideoRef.current.srcObject = stream;
      screenVideoRef.current.play();
      stream.getVideoTracks()[0].onended = () => setScreenActive(false);
      setScreenActive(true);
    } catch (e) {
      console.log('Screen share cancelado');
    }
  }, [screenActive]);

  const captureScreen = useCallback(() => {
    if (!screenStreamRef.current) return null;
    const video  = screenVideoRef.current;
    const canvas = screenCanvasRef.current;
    canvas.width  = video.videoWidth  || 1280;
    canvas.height = video.videoHeight || 720;
    canvas.getContext('2d').drawImage(video, 0, 0);
    return canvas.toDataURL('image/png').split(',')[1];
  }, []);

  if (isOverlay) {
    return (
      <div className="h-screen bg-[#030308] flex items-center justify-center" data-testid="athena-overlay-bg">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center">
          <div className="w-3 h-3 rounded-full bg-blue-500/50 animate-pulse mx-auto mb-4" />
          <p className="text-xs text-white/20 font-mono tracking-widest uppercase">Athena em modo ambiente</p>
          <button onClick={() => setIsOverlay(false)} className="mt-6 text-[10px] text-blue-400/30 hover:text-blue-400/60 font-mono transition-colors" data-testid="restore-from-overlay">
            Restaurar
          </button>
        </motion.div>
      </div>
    );
  }

  // Derived states for orb
  const orbListening = voiceState === VOICE_ACTIVE;
  const orbSpeaking = voiceState === VOICE_SPEAKING;
  const orbProcessing = false; // CognitiveSpace fornece o cue visual — Orb mantém cor padrão

  return (
    <LifeOSProvider cacheOwnerId={user?.id} surface="home">
    <div className="athena-home-page bg-[#030308] flex flex-col overflow-hidden relative" data-testid="athena-page" style={{
      paddingTop: 'var(--athena-safe-top)',
      paddingRight: 'var(--athena-safe-right)',
      paddingBottom: 'var(--athena-safe-bottom)',
      paddingLeft: 'var(--athena-safe-left)',
    }}>
      <AthenaWorkspaceBg state={chatBgState}/>
      <AnimatePresence>
        {showCognitive && (
          <motion.div
            key="cognitive-space"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: { duration: 0.45 } }}
            exit={{ opacity: 0, transition: { duration: 0.55 } }}
          style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none' }}
          >
            <AthenaThinkingVideoBackground index={thinkingVideoIndex} />
            <AthenaCognitiveSpace />
          </motion.div>
        )}
      </AnimatePresence>
      {/* Header */}
      <header className="flex items-center justify-between px-6 py-4 relative z-10">
        <div className="flex items-center gap-3">
          {/* State indicator - purely visual, no text */}
          <div className={`w-2 h-2 rounded-full transition-all duration-1000 ${
            voiceState === VOICE_SPEAKING ? 'bg-purple-400 shadow-[0_0_8px_rgba(192,132,252,0.6)]' :
            voiceState === VOICE_ACTIVE ? 'bg-green-400 shadow-[0_0_8px_rgba(74,222,128,0.6)]' :
            voiceState === VOICE_PROCESSING ? 'bg-amber-400 shadow-[0_0_8px_rgba(251,191,36,0.5)]' :
            voiceState === VOICE_SILENT ? 'bg-red-400/50' :
            voiceState === VOICE_FOCUS ? 'bg-cyan-400/50' :
            voiceState === VOICE_PASSIVE ? 'bg-blue-500/40' : 'bg-white/15'
          }`} />
        </div>
        {/* Logo — centered */}
        <div style={{position:'absolute',left:'50%',transform:'translateX(-50%)',pointerEvents:'none'}}>
          <AthenaLogo size={26} showText={true}/>
        </div>
        {/* Desktop: todos os ícones visíveis */}
        <div className="hidden sm:flex items-center gap-1">
          <button onClick={newConversation} className="p-2 rounded-lg hover:bg-white/5 transition-colors" title="Nova conversa">
            <Plus size={15} className="text-white/20 hover:text-white/50" />
          </button>
          {HOME_ACTION_BAR_FEATURES.attachments && (
            <button onClick={() => setShowUpload(true)} className="p-2 rounded-lg hover:bg-white/5 transition-colors">
              <PaperclipHorizontal size={15} className="text-white/20 hover:text-white/50" />
            </button>
          )}
          <button onClick={() => setShowValley(true)} className="p-2 rounded-lg hover:bg-white/5 transition-colors" title="Valley of Memories">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-white/20 hover:text-white/50">
              <circle cx="12" cy="12" r="3"/><path d="M12 2v2m0 16v2M4.93 4.93l1.41 1.41m11.32 11.32l1.41 1.41M2 12h2m16 0h2M4.93 19.07l1.41-1.41m11.32-11.32l1.41-1.41"/>
            </svg>
          </button>
          <button onClick={() => setShowChat(v => !v)} className="p-2 rounded-lg hover:bg-white/5 transition-colors" title="Histórico">
            <ChatTeardropDots size={15} className={showChat ? 'text-blue-400/40' : 'text-white/20 hover:text-white/50'} />
          </button>
          {HOME_ACTION_BAR_FEATURES.visualMode && (
            <button onClick={toggleScreen} title={screenActive ? "Desativar visão de tela" : "Ativar visão de tela"}
              style={{ background: screenActive ? 'rgba(0,200,150,0.2)' : 'rgba(255,255,255,0.05)', border: `1px solid ${screenActive ? '#00c896' : 'rgba(255,255,255,0.1)'}`, borderRadius: '50%', width: 32, height: 32, color: screenActive ? '#00c896' : '#666', cursor: 'pointer', fontSize: 14 }}>👁</button>
          )}
          <button onClick={() => navigate('/visual-call')} title="Chamada visual com Athena"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '50%', width: 32, height: 32, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', color: 'rgba(255,255,255,0.35)' }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>
            </svg>
          </button>
          <button onClick={openWorkspace} className="p-2 rounded-lg hover:bg-white/5 transition-colors" title="Área de Trabalho">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-white/20 hover:text-white/50">
              <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/>
              <rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>
            </svg>
          </button>
          {HOME_ACTION_BAR_FEATURES.fullscreen && (
            <button onClick={() => setIsOverlay(true)} className="p-2 rounded-lg hover:bg-white/5 transition-colors">
              <ArrowsIn size={15} className="text-white/20 hover:text-white/50" />
            </button>
          )}
          <button onClick={logout} className="p-2 rounded-lg hover:bg-white/5 transition-colors">
            <SignOut size={15} className="text-white/20 hover:text-white/50" />
          </button>
        </div>

        {/* Mobile: ícone único ≡ */}
        <button onClick={() => setShowMobileMenu(true)} className="flex sm:hidden p-2 rounded-xl hover:bg-white/5 transition-colors">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <line x1="3" y1="6" x2="21" y2="6" stroke="rgba(255,255,255,0.35)"/>
            <line x1="3" y1="12" x2="21" y2="12" stroke="rgba(255,255,255,0.35)"/>
            <line x1="3" y1="18" x2="21" y2="18" stroke="rgba(255,255,255,0.35)"/>
          </svg>
        </button>
      </header>
      {notificationPermission === 'default' && messages.some((message) => message.role === 'user') && (
        <div style={{
          position: 'fixed', top: 76, left: '50%', transform: 'translateX(-50%)', zIndex: 60,
          width: 'min(420px, calc(100% - 32px))', padding: '12px 14px', borderRadius: 14,
          background: 'rgba(8,18,38,0.94)', border: '1px solid rgba(56,189,248,0.35)',
          boxShadow: '0 14px 40px rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <span style={{ fontSize: 19 }}>🔔</span>
          <span style={{ flex: 1, color: 'rgba(255,255,255,0.82)', fontSize: 12, lineHeight: 1.35 }}>
            Permita notificações para a Athena avisar você sobre lembretes e mensagens importantes.
          </span>
          <button onClick={enableNotifications} style={{
            border: 0, borderRadius: 9, padding: '8px 10px', cursor: 'pointer', fontWeight: 650,
            color: '#fff', background: '#1677ee', fontSize: 12, whiteSpace: 'nowrap',
          }}>Permitir</button>
        </div>
      )}

      {/* PRESENCE LAYER */}
      <main className="flex-1 flex flex-col items-center justify-center relative" onClick={voiceState === VOICE_SPEAKING ? (e) => {
        if (!e.target.closest('[data-voice-orb]')) handleInterrupt();
      } : undefined}>
        <HomeContextCards ownerId={user?.id} onOpenToday={() => handleCoreMenuAction('today')} />
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-blue-900/5 blur-3xl" />
        </div>

        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }} className="text-center mb-6 relative z-10">
          {messages.length > 0 ? (
            <>
              <h1 className="text-3xl sm:text-4xl font-extralight font-['Outfit'] text-white/90 tracking-tight">
                Bom te ver de novo, <span className="text-blue-400">{user?.name || 'User'}</span>
              </h1>
              <p className="text-sm text-white/25 mt-2 font-light">Retomando de onde paramos...</p>
            </>
          ) : (
            <>
              <h1 className="text-3xl sm:text-4xl font-extralight font-['Outfit'] text-white/90 tracking-tight">
                {greeting}, <span className="text-blue-400">{user?.name || 'User'}</span>
              </h1>
              <p className="text-sm text-white/25 mt-2 font-light">Como posso te ajudar hoje?</p>
            </>
          )}
        </motion.div>

        {/* Orb gesture wrapper — Phase 1: double-tap+hold 3s → voice; Phase 2: hold+drag → navigate */}
        <>
          <style>{`@keyframes wakeBreath{0%,100%{transform:scale(1);opacity:0.75}50%{transform:scale(1.08);opacity:1}}`}</style>

          {/* Phase 2: blur + dark overlay — duplo-toque cancela nav mode */}
          {dragReady && (
            <div
              onPointerDown={handleBgPointerDown}
              style={{
                position: 'fixed', inset: 0, zIndex: 40,
                background: 'rgba(0,0,0,0.42)',
                backdropFilter: 'blur(7px)', WebkitBackdropFilter: 'blur(7px)',
                cursor: 'pointer',
              }}
            />
          )}

          {/* Phase 2: left arrow — Chamada Visual */}
          {dragReady && (
            <div style={{
              position: 'fixed', left: 20, top: '50%', transform: 'translateY(-50%)',
              zIndex: 52, pointerEvents: 'none',
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
              opacity: dragTarget === 'visual-call' ? 1 : 0.35,
              transition: 'opacity 0.18s ease',
            }}>
              <span style={{ fontSize: 28, color: '#60a5fa', lineHeight: 1 }}>←</span>
              <span style={{
                fontSize: 10,
                color: dragTarget === 'visual-call' ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.5)',
                letterSpacing: '.09em', textTransform: 'uppercase',
                writingMode: 'vertical-rl', textOrientation: 'mixed',
                fontWeight: dragTarget === 'visual-call' ? 600 : 400,
                transition: 'color 0.18s ease',
              }}>Chamada Visual</span>
            </div>
          )}

          {/* Phase 2: right arrow — Área de Trabalho */}
          {dragReady && (
            <div style={{
              position: 'fixed', right: 20, top: '50%', transform: 'translateY(-50%)',
              zIndex: 52, pointerEvents: 'none',
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
              opacity: dragTarget === 'workspace' ? 1 : 0.35,
              transition: 'opacity 0.18s ease',
            }}>
              <span style={{ fontSize: 28, color: '#60a5fa', lineHeight: 1 }}>→</span>
              <span style={{
                fontSize: 10,
                color: dragTarget === 'workspace' ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.5)',
                letterSpacing: '.09em', textTransform: 'uppercase',
                writingMode: 'vertical-rl', textOrientation: 'mixed',
                fontWeight: dragTarget === 'workspace' ? 600 : 400,
                transition: 'color 0.18s ease',
              }}>Área de Trabalho</span>
            </div>
          )}

          {/* Orb container — transform driven by orbContainerRef (no state re-render per frame) */}
          <div
            ref={orbContainerRef}
            style={{
              position: 'relative', display: 'inline-flex',
              alignItems: 'center', justifyContent: 'center',
              willChange: 'transform',
              zIndex: dragReady ? 51 : 'auto',
            }}
          >
            {/* pointer-events:none: NeuralOrb's internal handlers never fire (no blue ring) */}
            <div style={{ pointerEvents: 'none' }}>
              <NeuralOrb isListening={orbListening} isSpeaking={orbSpeaking} isProcessing={orbProcessing} voiceLevel={voiceLevel}
                onTouchActivate={() => {}} continuousMode={continuousMode} />
            </div>
            {/* Circular hitbox — Phase 1+2 unified gesture entry point */}
            <button
              type="button"
              data-voice-orb
              aria-label={voiceState === VOICE_ACTIVE ? 'Parar e enviar voz' : 'Ativar microfone'}
              title={voiceState === VOICE_ACTIVE ? 'Parar e enviar voz' : 'Toque para falar. Mantenha pressionado para abrir o menu.'}
              // The voice hitbox still has one click path. A held pointer only
              // arms the discoverable radial menu and consumes that final click.
              onClick={voiceMode === 'manual' || voiceMode === 'continuous' ? handleCoreOrbClick : undefined}
              onPointerDown={voiceMode === 'manual' || voiceMode === 'continuous' ? startCoreMenuHold : handleOrbPointerDown}
              onPointerMove={voiceMode === 'manual' || voiceMode === 'continuous' ? undefined : handleOrbPointerMove}
              onPointerUp={voiceMode === 'manual' || voiceMode === 'continuous' ? clearCoreMenuHold : handleOrbPointerUp}
              onPointerLeave={voiceMode === 'manual' || voiceMode === 'continuous' ? clearCoreMenuHold : handleOrbPointerUp}
              onPointerCancel={voiceMode === 'manual' || voiceMode === 'continuous' ? clearCoreMenuHold : handleOrbPointerUp}
              onTouchMove={voiceMode === 'manual' || voiceMode === 'continuous' ? undefined : handleOrbTouchMove}
              style={{
                position: 'absolute',
                // O canvas do núcleo tem 320px. Uma área de 220px deixava as
                // bordas visíveis sem resposta ao toque, sobretudo em telas
                // móveis onde o usuário toca no anel pulsante.
                width: 320, height: 320,
                borderRadius: '50%',
                padding: 0,
                border: 0,
                background: 'transparent',
                cursor: 'pointer',
                touchAction: voiceMode === 'manual' || voiceMode === 'continuous' ? 'manipulation' : 'none',
                WebkitUserSelect: 'none',
                userSelect: 'none',
              }}
            />
            <button
              type="button"
              onClick={() => setCoreMenuOpen(true)}
              aria-haspopup="menu"
              aria-expanded={coreMenuOpen}
              aria-label="Abrir menu da Athena"
              style={{ position: 'absolute', bottom: -38, minHeight: 44, padding: '8px 14px', borderRadius: 22, border: '1px solid rgba(113,206,255,.26)', background: 'rgba(7,22,49,.7)', color: 'rgba(218,243,255,.74)', cursor: 'pointer', fontSize: 11, letterSpacing: '.04em' }}
            >
              Menu
            </button>
            {/* Wake glow — teal for voice, blue for nav mode */}
            {wakeActive && (
              <div style={{
                position: 'absolute',
                width: 110, height: 110,
                borderRadius: '50%', pointerEvents: 'none',
                background: wakeMode === 'nav'
                  ? `radial-gradient(circle, rgba(96,165,250,${0.28 + wakeProgress * 0.52}) 0%, rgba(139,92,246,${0.10 + wakeProgress * 0.24}) 44%, transparent 72%)`
                  : `radial-gradient(circle, rgba(0,229,170,${0.28 + wakeProgress * 0.52}) 0%, rgba(0,200,212,${0.10 + wakeProgress * 0.24}) 44%, transparent 72%)`,
                boxShadow: wakeMode === 'nav'
                  ? `0 0 ${4 + wakeProgress * 16}px ${1 + wakeProgress * 6}px rgba(96,165,250,${0.07 + wakeProgress * 0.18})`
                  : `0 0 ${4 + wakeProgress * 16}px ${1 + wakeProgress * 6}px rgba(0,229,170,${0.07 + wakeProgress * 0.18})`,
                opacity: 0.45 + wakeProgress * 0.55,
                animation: 'wakeBreath 0.65s ease-in-out infinite',
              }} />
            )}
          </div>
        </>

        <div style={{ height: 28, marginTop: 6 }} />

        {/* Last response - ambient text */}
        <AnimatePresence>
          {lastResponse && !lastGeneratedImage && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: lastResponseIsError ? 0.96 : 0.5 }} exit={{ opacity: 0 }}
              className="absolute z-10 bottom-28 left-1/2 w-[min(32rem,calc(100vw-2rem))] -translate-x-1/2 px-3 text-center">
              <div className={lastResponseIsError
                ? 'rounded-2xl border border-rose-300/30 bg-slate-950/75 px-4 py-3 text-sm text-slate-100 shadow-[0_10px_38px_rgba(0,0,0,0.35)] backdrop-blur-sm'
                : 'text-sm text-white/40 font-light leading-relaxed line-clamp-3'}>
                {lastResponseIsError && (
                  <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-rose-200/85">
                    Resposta indisponível
                  </div>
                )}
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={{
                  p: ({children}) => <span style={{display:'block'}}>{children}</span>,
                  h1: ({children}) => <span style={{fontWeight:600}}>{children}</span>,
                  h2: ({children}) => <span style={{fontWeight:600}}>{children}</span>,
                  h3: ({children}) => <span style={{fontWeight:500}}>{children}</span>,
                  strong: ({children}) => <strong>{children}</strong>,
                  em: ({children}) => <em>{children}</em>,
                  code: ({children}) => <code style={{fontFamily:'monospace',fontSize:'0.9em'}}>{children}</code>,
                  ul: ({children}) => <span>{children}</span>,
                  ol: ({children}) => <span>{children}</span>,
                  li: ({children}) => <span style={{display:'block'}}>• {children}</span>,
                }}>{lastResponse}</ReactMarkdown>
                {lastResponseIsError && lastFailedMessage && (
                  <button
                    type="button"
                    onClick={() => { void sendMessage(lastFailedMessage); }}
                    className="mt-3 rounded-full border border-cyan-300/35 bg-cyan-300/10 px-3 py-1.5 text-xs font-medium text-cyan-100 transition hover:bg-cyan-300/20 focus:outline-none focus:ring-2 focus:ring-cyan-300/60"
                  >
                    Tentar novamente
                  </button>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {lastGeneratedImage && (
            <motion.div
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
              className="absolute bottom-32 left-1/2 -translate-x-1/2 w-[min(28rem,calc(100vw-3rem))]"
            >
              <ImageGenerationCard
                imageUrl={lastGeneratedImage.imageUrl}
                prompt={lastGeneratedImage.prompt}
                provider={lastGeneratedImage.provider}
                isMobile={IS_MOBILE}
              />
              {lastResponse && (
                <div style={{
                  marginTop: 8,
                  padding: '8px 10px',
                  borderRadius: 10,
                  color: 'rgba(226,232,240,.76)',
                  background: 'rgba(4,12,29,.66)',
                  border: '1px solid rgba(96,165,250,.12)',
                  fontSize: 12,
                  lineHeight: 1.45,
                  maxHeight: 76,
                  overflow: 'auto',
                }}>
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{lastResponse}</ReactMarkdown>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* O mesmo espaço alterna entre a Home completa e a entrada compacta.
          A Athena rápida reaproveita a conversa, voz e ações verificáveis da Home. */}
      {!quickAthenaActive && <HomeVoiceComposer
        textInput={textInput}
        onTextInputChange={setTextInput}
        onSubmit={handleTextSubmit}
        onVoiceToggle={handleVoiceButton}
        voiceState={voiceState}
        voiceMode={voiceMode}
        voiceLevel={voiceLevel}
        sessionRestoring={sessionRestoring}
        keyboardOpen={homeKeyboardOpen && !IS_NATIVE_APP}
        suppressDeviceContext={homeKeyboardOpen}
        inputRef={homeTextInputRef}
        pendingDeviceItems={pendingDeviceItems}
        onUseSharedText={handleUseSharedText}
        onOpenSharedAttachments={handleOpenSharedAttachments}
        onDiscardSharedContext={handleDiscardSharedContext}
      />}

      <QuickAthenaPanel
        intent={pendingIntent}
        lastResponse={lastResponse}
        voiceState={voiceState}
        voiceLevel={voiceLevel}
        sessionRestoring={sessionRestoring}
        onSubmit={handleQuickAthenaSubmit}
        onVoiceToggle={handleVoiceButton}
        onDismiss={closeQuickAthena}
        onExpand={expandQuickAthena}
      />

      <UploadWindow isOpen={showUpload} onClose={() => setShowUpload(false)} token={token}
        onAnalysisComplete={(response, filename) => {
          setMessages(prev => [...prev, { role: 'user', content: `[Enviou: ${filename}]` }, { role: 'assistant', content: response }]);
          setLastResponse(response);
          speakText(response);
        }} />

      <ValleyOfMemories isOpen={showValley} onClose={() => setShowValley(false)} token={token} onSelectSession={loadSession} />

      <HolographicLocation isOpen={showLocation} onClose={() => setShowLocation(false)} locationData={locationData} />

      <AnimatePresence>
        {showChat && <ChatWindow messages={messages} sessionId={sessionId} onClose={() => setShowChat(false)} />}
      </AnimatePresence>

      {/* Mobile bottom sheet menu */}
      <AnimatePresence>
        {showMobileMenu && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setShowMobileMenu(false)}
              style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', zIndex:50, backdropFilter:'blur(4px)' }}
            />
            {/* Sheet */}
            <motion.div
              initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
              transition={{ type:'spring', damping:28, stiffness:300 }}
              style={{
                position:'fixed', bottom:0, left:0, right:0, zIndex:51,
                background:'rgba(10,11,22,0.6)', backdropFilter:'blur(20px)',
                WebkitBackdropFilter:'blur(20px)',
                borderTop:'1px solid rgba(255,255,255,0.08)',
                borderRadius:'20px 20px 0 0', padding:'8px 0 32px',
              }}
            >
              {/* Drag handle */}
              <div style={{ width:40, height:4, background:'rgba(255,255,255,0.15)', borderRadius:2, margin:'8px auto 20px' }} />

              {[
                { icon: <Plus size={18}/>, label: 'Nova conversa', fn: () => { newConversation(); setShowMobileMenu(false); } },
                { icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="3"/><path d="M12 2v2m0 16v2M4.93 4.93l1.41 1.41m11.32 11.32l1.41 1.41M2 12h2m16 0h2M4.93 19.07l1.41-1.41m11.32-11.32l1.41-1.41"/></svg>, label: 'Valley of Memories', fn: () => { setShowValley(true); setShowMobileMenu(false); } },
                { icon: <ChatTeardropDots size={18}/>, label: 'Histórico', fn: () => { setShowChat(v => !v); setShowMobileMenu(false); }, active: showChat },
                { icon: <Microphone size={18}/>, label: 'Modo contínuo — em preparação', disabled: true },
                { icon: <Microphone size={18}/>, label: 'Modo Manual', fn: () => { selectVoiceMode('manual'); setShowMobileMenu(false); }, active: voiceMode === 'manual' },
                { icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></svg>, label: 'Chamada visual', fn: () => { setShowMobileMenu(false); navigate('/visual-call'); } },
                { icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>, label: 'Área de Trabalho', fn: () => { openWorkspace(); setShowMobileMenu(false); } },
                { icon: <SignOut size={18}/>, label: 'Sair', fn: () => { setShowMobileMenu(false); logout(); }, danger: true },
              ].map(item => (
                <button key={item.label} onClick={item.disabled ? undefined : item.fn} disabled={item.disabled} style={{
                  display:'flex', alignItems:'center', gap:16, width:'100%',
                  padding:'14px 24px', background:'none', border:'none', cursor:item.disabled ? 'not-allowed' : 'pointer',
                  color: item.disabled ? 'rgba(255,255,255,0.28)' : item.danger ? '#f87171' : item.active ? '#60a5fa' : 'rgba(255,255,255,0.6)',
                  fontSize:15, textAlign:'left', opacity: item.disabled ? 0.7 : 1,
                }}
                  onTouchStart={item.disabled ? undefined : (e => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; })}
                  onTouchEnd={item.disabled ? undefined : (e => { e.currentTarget.style.background = 'none'; })}
                >
                  <span style={{ opacity: item.active ? 1 : 0.7 }}>{item.icon}</span>
                  {item.label}
                  {item.active && <span style={{ marginLeft:'auto', fontSize:11, color:'#60a5fa', opacity:0.8 }}>ativo</span>}
                </button>
              ))}
            </motion.div>
          </>
        )}
      </AnimatePresence>


      {/* Toast mínimo para erros de câmera */}
      {homeToast && (
        <div style={{
          position: 'fixed', bottom: 'calc(88px + var(--athena-home-keyboard-offset, 0px))', left: '50%', transform: 'translateX(-50%)',
          background: 'rgba(10,14,30,0.92)', border: '1px solid rgba(59,130,246,0.25)',
          borderRadius: 12, padding: '10px 20px', fontSize: 13,
          color: 'rgba(255,255,255,0.75)', zIndex: 9997, backdropFilter: 'blur(8px)',
          whiteSpace: 'nowrap', maxWidth: '90vw', textAlign: 'center',
        }}>
          {homeToast}
        </div>
      )}
      <CoreRadialMenu
        open={coreMenuOpen}
        onClose={() => setCoreMenuOpen(false)}
        onAction={handleCoreMenuAction}
      />
    </div>
    </LifeOSProvider>
  );
}
